import LanguagePicker from '../i18n/LanguagePicker';
import CountryFlag from './CountryFlag';
import {t, useLanguage} from '../i18n';
import {cityById} from '../utils/regionalCities';
import {CityChoice} from "./RegionalFields";
import React, {useEffect,useRef,useState} from 'react';
import {createPortal} from 'react-dom';
import {FiMapPin,FiChevronDown,FiCheck,FiX,FiArrowRight,FiGlobe} from 'react-icons/fi';
import './MarketPicker.css';
import {onAuthStateChanged} from 'firebase/auth';
import {auth} from '../firebase';
import fsClient from '../data/firestoreClient';
import {getEffectiveManualAuthSession} from '../utils/manualAuth';
import {markets,readSearchMarket,saveSearchMarket,savedSearchMarket,deviceMarket,readSearchCity,readSearchCityId} from '../utils/market';
const userId=()=>auth?.currentUser?.uid || getEffectiveManualAuthSession()?.user?.uid || null;
export default function MarketPicker() {
  const language=useLanguage();
  const [cityId,setCityId]=useState(readSearchCityId);
  const [open,setOpen]=useState(false);
  const [draft,setDraft]=useState(readSearchMarket),[city,setCity]=useState(readSearchCity);
  useEffect(()=>{const show=()=>{setDraft(readSearchMarket());setCity(readSearchCity());setCityId(readSearchCityId());setOpen(true);};window.addEventListener('loop-open-market',show);return()=>window.removeEventListener('loop-open-market',show);},[]);
  const trigger=useRef(null),dialog=useRef(null);
  useEffect(()=>{
    if(!open)return;
    const previous=document.activeElement;
    const previousOverflow=document.body.style.overflow;
    document.body.style.overflow='hidden';
    dialog.current?.querySelector('button')?.focus();
    const keys=e=>{
      if(e.key==='Escape'){setOpen(false);return;}
      if(e.key==='Tab'){
        const buttons=[...dialog.current.querySelectorAll('button:not(:disabled),input:not(:disabled),select:not(:disabled)')];
        const first=buttons[0],last=buttons[buttons.length-1];
        if(e.shiftKey && document.activeElement===first){e.preventDefault();last.focus();}
        else if(!e.shiftKey && document.activeElement===last){e.preventDefault();first.focus();}
      }
    };
    document.addEventListener('keydown',keys);
    return()=>{document.removeEventListener('keydown',keys);document.body.style.overflow=previousOverflow;previous?.focus?.();};
  },[open]);
  const [market,setMarket]=useState(readSearchMarket),[busy,setBusy]=useState(false);
  const selectedHere=useRef(false);
  useEffect(()=>{
    let cancelled=false,loadedUid=null;
    const restore=async()=>{
      const uid=userId();if(!uid || loadedUid===uid || selectedHere.current)return;loadedUid=uid;
      try{
        const profile=await fsClient.getDocByPath(`users/${uid}`);
        const code=profile?.preferredSearchCountry;
        if(cancelled || selectedHere.current || userId()!==uid || !markets.some(item=>item.code===code))return;
        if(readSearchMarket().code!==code || readSearchCity()!==String(profile?.preferredSearchCity || '') || (cityById(code,profile?.preferredSearchCityId) && readSearchCityId()!==profile.preferredSearchCityId)){saveSearchMarket(code,profile?.preferredSearchCity || '',profile?.preferredSearchCityId);window.location.reload();}
      }catch(error){console.warn('[MarketPicker] country preference unavailable',error?.code);}
    };
    const unsubscribe=onAuthStateChanged(auth,restore);
    window.addEventListener('loop-auth-session-changed',restore);restore();
    return()=>{cancelled=true;unsubscribe();window.removeEventListener('loop-auth-session-changed',restore);};
  },[]);
  const select=async code=>{
    selectedHere.current=true;setBusy(true);setMarket(saveSearchMarket(code,city,cityId));
    const uid=userId();
    if(uid){
      let timer;
      try{await Promise.race([fsClient.setDocByPath(`users/${uid}`,{preferredSearchCountry:code,preferredSearchCity:city.trim().slice(0,100),preferredSearchCityId:readSearchCityId()}),new Promise((_,reject)=>{timer=setTimeout(()=>reject(new Error('timeout')),4000);})]);}
      catch(error){console.warn('[MarketPicker] saved locally; sync unavailable',error?.code);}
      finally{clearTimeout(timer);}
    }
    window.location.reload();
  };
  useEffect(()=>{
    if(savedSearchMarket() || deviceMarket())return;
    let cancelled=false;
    const controller=new AbortController();
    const timer=setTimeout(()=>controller.abort(),3000);
    try { if(sessionStorage.getItem('loop-country-lookup')){clearTimeout(timer);return;} sessionStorage.setItem('loop-country-lookup','1'); } catch {}
    fetch('https://www.loopreservas.com/api/market-country',{signal:controller.signal,credentials:'omit'})
      .then(r=>r.ok?r.json():null).then(data=>{
        if(cancelled || selectedHere.current || savedSearchMarket() || !markets.some(m=>m.code===data?.countryCode))return;
        saveSearchMarket(data.countryCode);window.location.reload();
      }).catch(()=>{}).finally(()=>clearTimeout(timer));
    return()=>{cancelled=true;clearTimeout(timer);controller.abort();};
  },[]);
  const known=savedSearchMarket() || deviceMarket();
  return <>
    <button ref={trigger} type="button" className="market-trigger" aria-label={t('País e idioma: {location}',{location:`${known ? market.name : t("Elegir país")}${readSearchCity() ? ', '+readSearchCity() : ''}`})} aria-haspopup="dialog" aria-expanded={open} onClick={()=>{setDraft(market);setCity(readSearchCity());setCityId(readSearchCityId());setOpen(true);}}>
      {known ? <CountryFlag code={market.code}/> : <FiMapPin aria-hidden="true"/>}<span className="market-trigger-copy"><span>{known ? market.name : t("Elegir país")}</span>{readSearchCity() && <small>{readSearchCity()}</small>}</span><span className="market-language-code">· {language==='pt-BR'?'PT':'ES'}</span><FiChevronDown aria-hidden="true"/>
    </button>
    {open && createPortal(<div className="market-backdrop" onMouseDown={e=>{if(e.target===e.currentTarget && !busy)setOpen(false);}}>
      <section ref={dialog} className="market-dialog" role="dialog" aria-modal="true" aria-labelledby="market-title" aria-describedby="market-description">
        <div className="market-dialog-heading"><span className="market-emblem" aria-hidden="true"><FiMapPin/></span><button type="button" aria-label={t("Cerrar selector de país")} onClick={()=>setOpen(false)}><FiX/></button></div>
        <span className="market-eyebrow">{t("TU UBICACIÓN")}</span><h2 id="market-title">{t("País e idioma")}</h2>
        <p id="market-description">{t("Elige dónde buscar y cómo quieres ver Loop.")}</p>
        <div className="market-language-section"><span>{t("Idioma")}</span><LanguagePicker/></div><div className="market-section-label">{t("Dónde buscas")}</div><div className="market-options">{markets.map(m=><button type="button" key={m.code} disabled={busy} aria-pressed={Boolean(draft.code===m.code)} onClick={()=>{selectedHere.current=true;setDraft(m);if(m.code!==draft.code){setCity('');setCityId(null);}}}>
          <CountryFlag code={m.code}/><span className="market-country-name">{m.name}</span><span className="market-selected-dot" aria-hidden="true">{draft.code===m.code ? <FiCheck/> : null}</span>
        </button>)}</div>
        <div className="market-city-section">
          <CityChoice label={t("Ciudad · opcional")} countryCode={draft.code} value={city} disabled={busy} onChange={(value,item)=>{selectedHere.current=true;setCity(value);setCityId(item?.id || null);}}/>
          {city && <button className="regional-manual" type="button" onClick={()=>{setCity('');setCityId(null);}}>{t('Explorar todo {country}',{country:draft.name})}</button>}
          <p className="market-city-help"><FiGlobe aria-hidden="true"/>{city.trim() ? t('Buscaremos en {city}, {country}.',{city:city.trim(),country:draft.name}) : t('Sin ciudad, puedes explorar todo {country}.',{country:draft.name})}</p>
        </div>
        <div className="market-footer">
          <button className="market-apply" type="button" disabled={busy} onClick={()=>select(draft.code)}><span>{busy ? t("Preparando tu búsqueda…") : t("Explorar servicios")}</span>{!busy && <FiArrowRight aria-hidden="true"/>}</button>
          <p className="market-note" role="status">{busy ? t("Guardando tu ubicación") : t("Puedes cambiar tu ubicación cuando quieras.")}</p>
        </div>
      </section>
    </div>,document.body)}
  </>;
}
